import { ProjectRepresentative } from '../types';
import { moveRepresentative, matchesRepresentative } from './projectRepresentatives';

/**
 * Besitzerwechsel über die Vertreterliste.
 *
 * HINTERGRUND (Vorfall 26081015, 20.08.2026)
 * Ein Projekt trägt seinen Verantwortlichen an ZWEI Stellen: in der Spalte
 * `projects.user_id` und im Bericht unter `metadata.coordinatorId`. Die RLS
 * lässt beide als Zugriffsgrund gelten, im Alltag fällt ein Auseinanderlaufen
 * deshalb nirgends auf — außer als rote „Fix"-Warnung in der Admin-Projektliste.
 *
 * Der Pfeil in der Vertreterliste schrieb bisher nur die JSON-Seite: wer eine
 * Person auf Position 0 schob, wechselte faktisch den Projekt-Besitzer, ohne
 * dass die Spalte mitging. Der Konflikt entstand also durch eine ganz normale,
 * beabsichtigte Bedienhandlung — und wurde erst Tage später als angeblicher
 * Datenfehler sichtbar.
 *
 * Diese Datei enthält die reine Entscheidung „ändert dieser Zug den
 * Hauptverantwortlichen, und lässt sich das überhaupt durchreichen?". Der
 * Schreibvorgang liegt bewusst nicht hier (Standing Decision: Logik in puren,
 * ohne DOM prüfbaren Funktionen).
 */

/** Dieselbe Person? id/E-Mail zuerst, Name nur als letzte Instanz. */
const sameRepresentative = (
    a: ProjectRepresentative | undefined,
    b: ProjectRepresentative | undefined
): boolean => {
    if (!a || !b) return a === b;
    if (matchesRepresentative(a, { id: b.id, email: b.email })) return true;
    const nameA = (a.name || '').trim().toLowerCase();
    return !!nameA && nameA === (b.name || '').trim().toLowerCase();
};

export interface OwnerChangePlan {
    /** Die Liste nach dem Zug. Identisch zur Eingabe, wenn nichts passiert. */
    next: ProjectRepresentative[];
    /** Wechselt dadurch der Hauptverantwortliche (Position 0)? */
    changesOwner: boolean;
    previousOwner?: ProjectRepresentative;
    newOwner?: ProjectRepresentative;
    /**
     * Die Benutzer-id, auf die `projects.user_id` gesetzt werden muss.
     * Leer, wenn der neue Hauptverantwortliche keinem Konto zugeordnet ist —
     * dann darf der Zug NICHT ausgeführt werden, denn Bericht und Spalte
     * liefen genau so auseinander, wie diese Datei verhindern soll.
     */
    transferTargetId?: string;
}

/**
 * Was passiert, wenn Eintrag `index` um `direction` verschoben wird?
 * Reine Vorschau — schreibt nichts.
 */
export const planOwnerChange = (
    reps: ProjectRepresentative[],
    index: number,
    direction: -1 | 1
): OwnerChangePlan => {
    const list = reps || [];
    const next = moveRepresentative(list, index, direction);

    // moveRepresentative gibt die Eingabe unverändert zurück, wenn der Zug an
    // einem Listenende ins Leere liefe.
    if (next === list) return { next: list, changesOwner: false };

    const previousOwner = list[0];
    const newOwner = next[0];
    if (sameRepresentative(previousOwner, newOwner)) {
        return { next, changesOwner: false, previousOwner, newOwner };
    }

    const targetId = (newOwner?.id || '').trim();
    return {
        next,
        changesOwner: true,
        previousOwner,
        newOwner,
        transferTargetId: targetId || undefined,
    };
};

/** Anzeigename eines Eintrags — nie leer, damit Dialoge lesbar bleiben. */
export const representativeLabel = (rep: ProjectRepresentative | undefined): string =>
    (rep?.name || '').trim() || (rep?.email || '').trim() || 'ohne Namen';

/**
 * Protokolltext des Wechsels.
 *
 * Bewusst NICHT der Wortlaut von `forceTransferByAdmin`
 * („SYSTEM: Projekt-Besitzer manuell angepasst durch Admin…"): auf dieses
 * Präfix stützt sich die Nachbetrachtung der August-Vorfälle
 * (supabase/diagnose_besitzerwechsel_august.sql). Ein eigener Text hält die
 * beiden Wege im Protokoll auseinander.
 */
export const ownerTransferNoteText = (fromName: string, toName: string): string =>
    `SYSTEM: Hauptverantwortlicher gewechselt von ${fromName} auf ${toName}. `
    + 'Der Projekt-Besitzer in der Datenbank wurde mitgeführt.';
